import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { FileText, Plus, Trash2, Edit, History, Lock } from 'lucide-react';
import { format } from 'date-fns';
import { useForm, useFieldArray } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useSecureMutation } from '@/hooks/useSecureMutation';
import { toast } from 'sonner';
import { ProductThumbnail } from '@/components/ui/product-thumbnail';
import { EditInvoiceDialog } from './EditInvoiceDialog';
import { InvoiceEditHistoryDialog } from './InvoiceEditHistoryDialog';
import { usePermissions } from '@/hooks/usePermissions';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

const invoiceSchema = z.object({
  invoice_number: z.string().optional(),
  customer_id: z.string().min(1, 'Please select a customer'),
  invoice_date: z.string().min(1, 'Invoice date is required'),
  due_date: z.string().min(1, 'Due date is required'),
  notes: z.string().optional(),
  items: z.array(z.object({
    inventory_item_id: z.string().uuid('Must select a product'),
    quantity: z.number().min(0.01, 'Quantity must be greater than 0'),
    unit_price: z.number().min(0, 'Unit price must be non-negative'),
  })).min(1, 'At least one item is required'),
});

type InvoiceFormData = z.infer<typeof invoiceSchema>;

export const InvoicesList = () => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editInvoiceId, setEditInvoiceId] = useState<string | null>(null);
  const [historyInvoiceId, setHistoryInvoiceId] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const { isSuperAdmin, userRole } = usePermissions();
  const canEditPrice = isSuperAdmin || userRole === 'admin';
  
  const { data: invoices = [], isLoading } = useQuery({
    queryKey: ['invoices'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('invoices')
        .select(`
          *,
          customer:customer_id(company_name),
          editor:last_edited_by(first_name, last_name, email),
          invoice_items (
            id,
            inventory_item_id,
            description,
            quantity,
            unit_price,
            total_price,
            image_url
          )
        `)
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      return data;
    },
  });

  const { data: customers = [] } = useQuery({
    queryKey: ['customers'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('customers')
        .select('id, company_name')
        .eq('status', 'active')
        .order('company_name');
      
      if (error) throw error;
      return data;
    },
  });

  const { data: inventoryItems = [] } = useQuery({
    queryKey: ['inventory-items-for-invoices'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('inventory_items')
        .select('id, name, sku, selling_price, image_url, current_stock, committed_stock')
        .eq('type', 'finished_product')
        .order('name');
      
      if (error) throw error;
      return data;
    },
  });

  const form = useForm<InvoiceFormData>({
    resolver: zodResolver(invoiceSchema),
    defaultValues: {
      invoice_date: new Date().toISOString().split('T')[0],
      due_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // 30 days from now
      items: [{ inventory_item_id: '', quantity: 1, unit_price: 0 }],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: 'items',
  });

  const createInvoiceMutation = useSecureMutation(
    async (data: InvoiceFormData) => {
      // Calculate totals
      const subtotal = data.items.reduce((sum, item) => sum + (item.quantity * item.unit_price), 0);
      const taxAmount = 0; // No tax for now
      const totalAmount = subtotal + taxAmount;

      // Use provided invoice number or generate one
      let invoiceNumber = data.invoice_number?.trim();
      
      if (!invoiceNumber) {
        const { data: invoiceNumberData, error: invoiceNumberError } = await supabase
          .rpc('generate_invoice_number');

        if (invoiceNumberError) throw invoiceNumberError;
        invoiceNumber = invoiceNumberData;
      }

      // Create the invoice
      const { data: newInvoice, error: invoiceError } = await supabase
        .from('invoices')
        .insert([{
          invoice_number: invoiceNumber,
          customer_id: data.customer_id,
          invoice_date: data.invoice_date,
          due_date: data.due_date,
          notes: data.notes,
          subtotal: subtotal,
          tax_amount: taxAmount,
          total_amount: totalAmount,
          paid_amount: 0,
          status: 'draft',
          created_by: (await supabase.auth.getUser()).data.user?.id,
        }])
        .select()
        .single();

      if (invoiceError) throw invoiceError;

      // Create invoice items with inventory links
      const invoiceItemsData = data.items.map(item => {
        const product = inventoryItems.find(inv => inv.id === item.inventory_item_id);
        return {
          invoice_id: newInvoice.id,
          inventory_item_id: item.inventory_item_id,
          description: product?.name || 'Unknown Product',
          image_url: product?.image_url,
          quantity: item.quantity,
          unit_price: item.unit_price,
          total_price: item.quantity * item.unit_price,
        };
      });

      const { error: itemsError } = await supabase
        .from('invoice_items')
        .insert(invoiceItemsData);

      if (itemsError) throw itemsError;

      // Now automatically create an order with "unapproved" status
      // Use the same number as the invoice for the order
      const orderNumber = invoiceNumber;

      // Create the order
      const { data: newOrder, error: orderError } = await supabase
        .from('orders')
        .insert([{
          order_number: orderNumber,
          customer_id: data.customer_id,
          order_date: data.invoice_date,
          expected_delivery_date: data.due_date,
          notes: `Auto-generated from invoice ${invoiceNumber}. ${data.notes || ''}`,
          subtotal: subtotal,
          tax_amount: taxAmount,
          total_amount: totalAmount,
          status: 'unapproved',
          invoice_id: newInvoice.id,
          created_by: (await supabase.auth.getUser()).data.user?.id,
        }])
        .select()
        .single();

      if (orderError) throw orderError;

      // Link invoice to order
      const { error: linkError } = await supabase
        .from('invoices')
        .update({ order_id: newOrder.id })
        .eq('id', newInvoice.id);

      if (linkError) throw linkError;

      // Create order items (now directly using inventory_item_id from invoice items)
      const orderItemsData = data.items.map(item => ({
        order_id: newOrder.id,
        inventory_item_id: item.inventory_item_id,
        quantity: item.quantity,
        unit_price: item.unit_price,
        total_price: item.quantity * item.unit_price,
      }));

      const { error: orderItemsError } = await supabase
        .from('order_items')
        .insert(orderItemsData);

      if (orderItemsError) throw orderItemsError;

      return { invoice: newInvoice, order: newOrder };
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['invoices'] });
        queryClient.invalidateQueries({ queryKey: ['orders'] });
        toast.success('Invoice created and order generated successfully');
        setIsDialogOpen(false);
        form.reset();
      },
      onError: (error: any) => {
        toast.error(`Failed to create invoice: ${error.message || 'Unknown error'}`);
        console.error('Error creating invoice:', error);
      },
    }
  );

  const onSubmit = (data: InvoiceFormData) => {
    createInvoiceMutation.mutate(data);
  };

  const watchedItems = form.watch('items');
  const totalAmount = watchedItems.reduce((sum, item) => sum + (item.quantity * item.unit_price), 0);

  const getStatusBadge = (status: string) => {
    const variants: Record<string, any> = {
      draft: 'secondary',
      sent: 'default',
      paid: 'default',
      overdue: 'destructive',
      cancelled: 'destructive',
    };
    
    return <Badge variant={variants[status] || 'outline'}>{status?.toUpperCase() || 'UNKNOWN'}</Badge>;
  };

  const getEditorName = (editor: any) => {
    if (!editor) return 'Unknown User';
    if (editor.first_name && editor.last_name) {
      return `${editor.first_name} ${editor.last_name}`;
    }
    return editor.email || 'Unknown User';
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex justify-between items-center">
          <div>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Invoices
            </CardTitle>
            <CardDescription>
              Customer invoices and payment tracking
            </CardDescription>
          </div>
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button>
                <Plus className="h-4 w-4 mr-2" />
                Create Invoice
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Create New Invoice</DialogTitle>
              </DialogHeader>
              <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                  <div className="grid grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name="invoice_number"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Invoice/Order Number (Optional)</FormLabel>
                          <FormControl>
                            <Input 
                              placeholder="Auto-generated if left empty" 
                              {...field} 
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="customer_id"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Customer</FormLabel>
                          <Select onValueChange={field.onChange} defaultValue={field.value}>
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder="Select customer" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {customers.map((customer) => (
                                <SelectItem key={customer.id} value={customer.id}>
                                  {customer.company_name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name="invoice_date"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Invoice Date</FormLabel>
                          <FormControl>
                            <Input type="date" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="due_date"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Due Date</FormLabel>
                          <FormControl>
                            <Input type="date" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <div className="space-y-4">
                    <div className="flex justify-between items-center">
                      <h3 className="text-lg font-medium">Invoice Items</h3>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => append({ inventory_item_id: '', quantity: 1, unit_price: 0 })}
                      >
                        <Plus className="h-4 w-4 mr-1" />
                        Add Item
                      </Button>
                    </div>

                    {fields.map((field, index) => {
                      const selectedProduct = inventoryItems.find(
                        inv => inv.id === watchedItems[index]?.inventory_item_id
                      );
                      const availableStock = selectedProduct 
                        ? (selectedProduct.current_stock - selectedProduct.committed_stock)
                        : 0;
                      
                      return (
                        <div key={field.id} className="space-y-2">
                          <div className="grid grid-cols-12 gap-2 items-end">
                            <div className="col-span-5">
                              <FormField
                                control={form.control}
                                name={`items.${index}.inventory_item_id`}
                                render={({ field }) => (
                                  <FormItem>
                                    <FormLabel className={index === 0 ? '' : 'sr-only'}>Product</FormLabel>
                                    <Select 
                                      onValueChange={(value) => {
                                        field.onChange(value);
                                        const product = inventoryItems.find(inv => inv.id === value);
                                        if (product) {
                                          form.setValue(`items.${index}.unit_price`, Number(product.selling_price));
                                        }
                                      }} 
                                      value={field.value}
                                    >
                                      <FormControl>
                                        <SelectTrigger>
                                          <SelectValue placeholder="Select product" />
                                        </SelectTrigger>
                                      </FormControl>
                                      <SelectContent>
                                        {inventoryItems.map((item) => (
                                          <SelectItem key={item.id} value={item.id}>
                                            <div className="flex items-center gap-2">
                                              {item.image_url && (
                                                <ProductThumbnail 
                                                  imageUrl={item.image_url}
                                                  name={item.name}
                                                  size="sm"
                                                />
                                              )}
                                              <span>{item.name} - ${item.selling_price}</span>
                                            </div>
                                          </SelectItem>
                                        ))}
                                      </SelectContent>
                                    </Select>
                                    <FormMessage />
                                  </FormItem>
                                )}
                              />
                            </div>
                            <div className="col-span-2">
                              <FormField
                                control={form.control}
                                name={`items.${index}.quantity`}
                                render={({ field }) => (
                                  <FormItem>
                                    <FormLabel className={index === 0 ? '' : 'sr-only'}>Quantity</FormLabel>
                                    <FormControl>
                                      <Input 
                                        type="number" 
                                        step="0.01"
                                        placeholder="1"
                                        {...field}
                                        onChange={(e) => field.onChange(parseFloat(e.target.value) || 0)}
                                      />
                                    </FormControl>
                                    <FormMessage />
                                  </FormItem>
                                )}
                              />
                            </div>
                            <div className="col-span-2">
                              <FormField
                                control={form.control}
                                name={`items.${index}.unit_price`}
                                render={({ field }) => (
                                  <FormItem>
                                    <FormLabel className={index === 0 ? '' : 'sr-only'}>
                                      <div className="flex items-center gap-1">
                                        Unit Price
                                        {!canEditPrice && (
                                          <TooltipProvider>
                                            <Tooltip>
                                              <TooltipTrigger asChild>
                                                <Lock className="h-3 w-3 text-muted-foreground" />
                                              </TooltipTrigger>
                                              <TooltipContent>
                                                <p>Only admins can override prices</p>
                                              </TooltipContent>
                                            </Tooltip>
                                          </TooltipProvider>
                                        )}
                                      </div>
                                    </FormLabel>
                                    <FormControl>
                                      <Input 
                                        type="number" 
                                        step="0.01"
                                        placeholder="0.00"
                                        disabled={!canEditPrice}
                                        {...field}
                                        onChange={(e) => field.onChange(parseFloat(e.target.value) || 0)}
                                      />
                                    </FormControl>
                                    <FormMessage />
                                  </FormItem>
                                )}
                              />
                            </div>
                            <div className="col-span-2">
                              <div className={`${index === 0 ? 'pt-6' : ''} text-sm font-medium`}>
                                ${((watchedItems[index]?.quantity || 0) * (watchedItems[index]?.unit_price || 0)).toFixed(2)}
                              </div>
                            </div>
                            <div className="col-span-1">
                              {fields.length > 1 && (
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  onClick={() => remove(index)}
                                  className={index === 0 ? 'mt-6' : ''}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              )}
                            </div>
                          </div>
                          {selectedProduct && availableStock < (watchedItems[index]?.quantity || 0) && (
                            <div className="text-xs text-amber-600 dark:text-amber-400 pl-1">
                              ⚠️ Available stock: {availableStock} (may not be sufficient)
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  <FormField
                    control={form.control}
                    name="notes"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Notes</FormLabel>
                        <FormControl>
                          <Textarea placeholder="Invoice notes or payment terms" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <div className="bg-muted p-4 rounded-lg space-y-2">
                    <div className="flex justify-between font-bold text-lg">
                      <span>Total:</span>
                      <span>${totalAmount.toFixed(2)}</span>
                    </div>
                  </div>

                  <div className="flex justify-end gap-2">
                    <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                      Cancel
                    </Button>
                    <Button type="submit" disabled={createInvoiceMutation.isPending}>
                      {createInvoiceMutation.isPending ? 'Creating...' : 'Create Invoice'}
                    </Button>
                  </div>
                </form>
              </Form>
            </DialogContent>
          </Dialog>
        </div>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Invoice #</TableHead>
              <TableHead>Customer</TableHead>
              <TableHead>Items</TableHead>
              <TableHead>Invoice Date</TableHead>
              <TableHead>Due Date</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Edit Status</TableHead>
              <TableHead>Total Amount</TableHead>
              <TableHead>Paid Amount</TableHead>
              <TableHead>Balance</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={11} className="text-center">Loading...</TableCell>
              </TableRow>
            ) : invoices.length === 0 ? (
              <TableRow>
                <TableCell colSpan={11} className="text-center">No invoices found</TableCell>
              </TableRow>
            ) : (
              invoices.map((invoice) => (
                <TableRow key={invoice.id}>
                  <TableCell className="font-medium">{invoice.invoice_number}</TableCell>
                  <TableCell>{(invoice.customer as any)?.company_name}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      {(invoice as any).invoice_items?.slice(0, 3).map((item: any) => (
                        <ProductThumbnail
                          key={item.id}
                          imageUrl={item.image_url}
                          name={item.description}
                          size="sm"
                        />
                      ))}
                      {(invoice as any).invoice_items?.length > 3 && (
                        <span className="text-xs text-muted-foreground">
                          +{(invoice as any).invoice_items.length - 3}
                        </span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    {format(new Date(invoice.invoice_date), 'MMM dd, yyyy')}
                  </TableCell>
                  <TableCell>
                    {format(new Date(invoice.due_date), 'MMM dd, yyyy')}
                  </TableCell>
                  <TableCell>{getStatusBadge(invoice.status)}</TableCell>
                  <TableCell>
                    {invoice.is_edited ? (
                      <div className="space-y-1">
                        <Badge variant="secondary" className="flex items-center gap-1 w-fit bg-amber-100 text-amber-900 dark:bg-amber-900 dark:text-amber-100">
                          <Edit className="h-3 w-3" />
                          EDITED
                        </Badge>
                        {invoice.last_edited_at && (
                          <div className="text-xs text-muted-foreground">
                            {format(new Date(invoice.last_edited_at), 'MMM d, h:mm a')}
                          </div>
                        )}
                        {invoice.editor && (
                          <div className="text-xs text-muted-foreground">
                            by {getEditorName(invoice.editor)}
                          </div>
                        )}
                      </div>
                    ) : (
                      <span className="text-muted-foreground text-sm">—</span>
                    )}
                  </TableCell>
                  <TableCell>${invoice.total_amount.toFixed(2)}</TableCell>
                  <TableCell>${invoice.paid_amount.toFixed(2)}</TableCell>
                  <TableCell>
                    ${(invoice.total_amount - invoice.paid_amount).toFixed(2)}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      {invoice.status === 'draft' && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setEditInvoiceId(invoice.id)}
                        >
                          <Edit className="h-4 w-4 mr-1" />
                          Edit
                        </Button>
                      )}
                      {invoice.is_edited && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setHistoryInvoiceId(invoice.id)}
                        >
                          <History className="h-4 w-4 mr-1" />
                          View History
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </CardContent>

      {/* Edit Invoice Dialog */}
      {editInvoiceId && (
        <EditInvoiceDialog
          invoiceId={editInvoiceId}
          open={!!editInvoiceId}
          onOpenChange={(open) => !open && setEditInvoiceId(null)}
        />
      )}

      {/* Invoice Edit History Dialog */}
      {historyInvoiceId && (
        <InvoiceEditHistoryDialog
          invoiceId={historyInvoiceId}
          open={!!historyInvoiceId}
          onOpenChange={(open) => !open && setHistoryInvoiceId(null)}
        />
      )}
    </Card>
  );
};